// animations/prefetch.js
import barba from '@barba/core';
// Préchargement d'un projet au survol : dès que la souris s'attarde sur une carte / un lien de projet
// (tout élément [data-url] : carrousel, index, Work, panneaux), on télécharge la page et on lance
// le chargement de sa photo de hero et des premières images de la galerie.
// Au clic, une partie du travail est déjà faite : la page s'ouvre plus vite, même pour un premier visiteur.

const HOVER_DELAY   = 90;  // ms de survol avant de lancer (évite de tout précharger en traversant la page)
const GALLERY_COUNT = 2;   // nombre d'images de galerie préchargées en plus du hero

const started = new Set();
const keep    = [];        // garde une référence aux Image() le temps du chargement

const sameOrigin = (url) => {
    try { return new URL(url, location.href).origin === location.origin; } catch { return false; }
};

// Lance le téléchargement d'une image avec les mêmes srcset/sizes que la page :
// le navigateur choisit la même variante et la réutilisera depuis son cache
const warmImage = (el) => {
    if (!el) return;
    const img = new Image();
    img.decoding = 'async';
    if (el.getAttribute('sizes'))  img.sizes  = el.getAttribute('sizes');
    if (el.getAttribute('srcset')) img.srcset = el.getAttribute('srcset');
    img.src = el.getAttribute('src');
    keep.push(img);
    if (keep.length > 40) keep.shift();
};

export const prefetchProject = async (url) => {
    if (!url || started.has(url) || !sameOrigin(url)) return;
    // Connexions lentes ou mode économie de données : on ne précharge pas
    const conn = navigator.connection;
    if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return;

    started.add(url);
    try {
        // La page passe par le cache de Barba : au clic, Barba la reprend telle quelle
        // au lieu de la redemander au serveur (c'est le gain principal pour un premier visiteur)
        const href = new URL(url, location.href).href;
        let html = '';
        if (typeof barba.prefetch === 'function' && barba.cache) {
            barba.prefetch(href);
            const res = await barba.cache.get(href)?.request;
            html = res?.html || '';
        }
        if (!html) {
            const res = await fetch(href, { credentials: 'same-origin' });
            if (!res.ok) { started.delete(url); return; }
            html = await res.text();
        }
        const doc = new DOMParser().parseFromString(html, 'text/html');

        warmImage(doc.querySelector('.article-featured-img'));
        [...doc.querySelectorAll('#article-content .img-projet')]
            .slice(0, GALLERY_COUNT)
            .forEach(warmImage);
    } catch {
        started.delete(url); // nouvel essai possible au prochain survol
    }
};

export const initProjectPrefetch = () => {
    if (window.__csPrefetch) return; // une seule fois (délégation sur le document, valable après Barba)
    window.__csPrefetch = true;

    let timer = null;
    let current = null;

    document.addEventListener('pointerover', (e) => {
        const el = e.target.closest?.('[data-url]');
        if (!el || el === current) return;
        current = el;
        clearTimeout(timer);
        timer = setTimeout(() => prefetchProject(el.dataset.url), HOVER_DELAY);
    }, { passive: true });

    document.addEventListener('pointerout', (e) => {
        const el = e.target.closest?.('[data-url]');
        if (!el || el.contains(e.relatedTarget)) return;
        if (el === current) { current = null; clearTimeout(timer); }
    }, { passive: true });

    // Tactile : le toucher précède le clic de quelques centaines de ms
    document.addEventListener('touchstart', (e) => {
        const el = e.target.closest?.('[data-url]');
        if (el) prefetchProject(el.dataset.url);
    }, { passive: true });
};
